import React, { useState, useEffect, useMemo } from 'react';
import { useUserPreferences } from './hooks/useLocalStorage';
import InvestmentInput from './components/InvestmentInput';
import SummaryCards from './components/SummaryCards';
import Controls from './components/Controls';
import InvestmentTable from './components/InvestmentTable';
import Assumptions from './components/Assumptions';
import ScenariosManager from './components/ScenariosManager';
import { generateInvestmentData } from './utils/investmentCalculations';

function App() {
  const { preferences, updatePreference } = useUserPreferences();
  
  const [initialInvestment, setInitialInvestment] = useState(() => 
    preferences.defaultInitialInvestment || 250000
  );
  const [targetNetValue, setTargetNetValue] = useState(() => 
    preferences.defaultTargetNetValue || 2000000
  );
  const [startDate, setStartDate] = useState(() => 
    preferences.defaultStartDate ? new Date(preferences.defaultStartDate) : new Date(2025, 0, 1)
  );
  const [endDate, setEndDate] = useState(() => 
    preferences.defaultEndDate ? new Date(preferences.defaultEndDate) : new Date(2029, 11, 28)
  );
  const [baseSalary, setBaseSalary] = useState(() => 
    preferences.defaultBaseSalary || 0
  );
  const [investmentData, setInvestmentData] = useState([]);
  const [yearFilter, setYearFilter] = useState(() => preferences.yearFilter || 'all');
  const [showFilter, setShowFilter] = useState(() => preferences.showFilter || 'all');
  const [currentWeekRow, setCurrentWeekRow] = useState(null);

  // Generate investment data whenever parameters change
  const data = useMemo(() => {
    return generateInvestmentData(initialInvestment, targetNetValue, endDate, startDate, baseSalary);
  }, [initialInvestment, targetNetValue, endDate, startDate, baseSalary]);

  // Update investment data when data changes
  useEffect(() => {
    setInvestmentData(data);
  }, [data]);

  // Find current week row for highlighting
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Calculate current week of the year
    const startOfYear = new Date(currentYear, 0, 1);
    const daysSinceStartOfYear = Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24));
    const currentWeekOfYear = Math.ceil((daysSinceStartOfYear + startOfYear.getDay() + 1) / 7);
    
    // Find the row that matches the current week
    const currentRow = data.find(row => 
      row.isWeeklyData && row.year === currentYear && row.week === currentWeekOfYear
    );
    
    setCurrentWeekRow(currentRow);
  }, [data]);

  const handleInvestmentChange = (newInvestment) => {
    setInitialInvestment(newInvestment);
    updatePreference('defaultInitialInvestment', newInvestment);
  };

  const handleTargetNetValueChange = (newTargetNetValue) => {
    setTargetNetValue(newTargetNetValue);
    updatePreference('defaultTargetNetValue', newTargetNetValue);
  };

  const handleEndDateChange = (newEndDate) => {
    setEndDate(newEndDate);
    updatePreference('defaultEndDate', newEndDate.toISOString());
  };

  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate);
    updatePreference('defaultStartDate', newStartDate.toISOString());
  };

  const handleBaseSalaryChange = (newBaseSalary) => {
    setBaseSalary(newBaseSalary);
    updatePreference('defaultBaseSalary', newBaseSalary);
  };

  const handleLoadScenario = (scenarioData) => {
    setInitialInvestment(scenarioData.initialInvestment);
    setTargetNetValue(scenarioData.targetNetValue);
    setStartDate(new Date(scenarioData.startDate));
    setEndDate(new Date(scenarioData.endDate));
    setBaseSalary(scenarioData.baseSalary || 0);
    setYearFilter(scenarioData.yearFilter || 'all');
    setShowFilter(scenarioData.showFilter || 'all');
    
    // Update preferences with loaded scenario
    updatePreference('defaultInitialInvestment', scenarioData.initialInvestment);
    updatePreference('defaultTargetNetValue', scenarioData.targetNetValue);
    updatePreference('defaultStartDate', scenarioData.startDate);
    updatePreference('defaultEndDate', scenarioData.endDate);
    updatePreference('yearFilter', scenarioData.yearFilter || 'all');
    updatePreference('showFilter', scenarioData.showFilter || 'all');
  };

  const getCurrentScenarioData = () => ({
    initialInvestment,
    targetNetValue,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    baseSalary,
    yearFilter,
    showFilter
  });

  const scrollToCurrentWeek = () => {
    if (currentWeekRow) {
      const element = document.getElementById(`week-${currentWeekRow.week}-${currentWeekRow.year}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Add flash effect
        element.style.boxShadow = '0 0 20px rgba(23, 162, 184, 0.6)';
        setTimeout(() => {
          element.style.boxShadow = '0 2px 4px rgba(23, 162, 184, 0.1)';
        }, 1000);
      }
    }
  };

  // Calculate summary values
  const summaryData = useMemo(() => {
    if (data.length === 0) return {};
    
    const totalWeeks = data.filter(row => row.isWeeklyData).length;
    
    // Calculate required annual return
    const requiredTotalReturn = targetNetValue / initialInvestment;
    const weeklyGrowthRate = Math.pow(requiredTotalReturn, 1/totalWeeks) - 1;
    const annualGrowthRate = Math.pow(1 + weeklyGrowthRate, 52) - 1;
    
    return {
      totalWeeks,
      requiredReturn: annualGrowthRate * 100
    };
  }, [data, initialInvestment, targetNetValue]);

  return (
    <div className="container">
      <div className="header">
          <h1>Investment Growth Plan</h1>
          <p>Path to ${targetNetValue.toLocaleString()}+ Net Portfolio Value by {endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} (Starting ${initialInvestment.toLocaleString()} from {startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - Taxes Applied All Years)</p>
        </div>
        
        <ScenariosManager 
          currentData={getCurrentScenarioData()}
          onLoadScenario={handleLoadScenario}
        />
        
        <InvestmentInput 
          initialInvestment={initialInvestment}
          onInvestmentChange={handleInvestmentChange}
          targetNetValue={targetNetValue}
          onTargetNetValueChange={handleTargetNetValueChange}
          startDate={startDate}
          onStartDateChange={handleStartDateChange}
          endDate={endDate}
          onEndDateChange={handleEndDateChange}
          baseSalary={baseSalary}
          onBaseSalaryChange={handleBaseSalaryChange}
        />
        
        <SummaryCards 
          initialInvestment={initialInvestment}
          totalWeeks={summaryData.totalWeeks}
          requiredReturn={summaryData.requiredReturn}
          targetNetValue={targetNetValue}
          startDate={startDate}
          endDate={endDate}
        />
        
        <Controls 
          yearFilter={yearFilter}
          showFilter={showFilter}
          onYearFilterChange={(value) => {
            setYearFilter(value);
            updatePreference('yearFilter', value);
          }}
          onShowFilterChange={(value) => {
            setShowFilter(value);
            updatePreference('showFilter', value);
          }}
          onScrollToCurrent={scrollToCurrentWeek}
        />
        
        <InvestmentTable 
          data={investmentData}
          yearFilter={yearFilter}
          showFilter={showFilter}
          currentWeekRow={currentWeekRow}
        />
        
        <Assumptions />
    </div>
  );
}

export default App;
